import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Lightning,
  Eye,
  ArrowClockwise,
  Circle,
  MapPin,
  Timer,
  ArrowsClockwise,
  WifiHigh,
  WifiSlash,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const LOGO_1 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/3xn9wu25_standby_transparent.png";
const LOGO_2 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/v4idzbss_on_site_transparent.png";

const TRIAGE_CONFIG = [
  { color: 'Green', bg: 'bg-green-500', border: 'border-green-500', text: 'text-white', label: 'Minor' },
  { color: 'Yellow', bg: 'bg-yellow-400', border: 'border-yellow-400', text: 'text-black', label: 'Delayed' },
  { color: 'Orange', bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-white', label: 'Urgent' },
  { color: 'Red', bg: 'bg-red-600', border: 'border-red-600', text: 'text-white', label: 'Immediate' },
  { color: 'Black', bg: 'bg-gray-900', border: 'border-gray-900', text: 'text-white', label: 'Expectant' },
];

function formatDuration(minutes) {
  if (minutes == null) return '--';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

function getDurationColor(minutes) {
  if (minutes == null) return 'text-slate-400';
  if (minutes < 30) return 'text-green-400';
  if (minutes < 60) return 'text-yellow-400';
  if (minutes < 120) return 'text-orange-400';
  return 'text-red-400';
}

export default function CommandPostPage() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);

  // Auto-switch state
  const [todaysEvents, setTodaysEvents] = useState([]);
  const [autoSwitch, setAutoSwitch] = useState(false);
  const autoSwitchIndex = useRef(0);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const fetchEvents = useCallback(async () => {
    try {
      const { data: evts } = await axios.get(`${API_URL}/api/events`, {});
      setEvents(evts);
    } catch (err) {
      console.warn('Event list fetch failed (non-critical):', err.message);
    }
  }, [token]);

  const fetchTodaysEvents = useCallback(async () => {
    try {
      const { data: tevts } = await axios.get(`${API_URL}/api/reports/todays-events`, {});
      setTodaysEvents(tevts);
    } catch (err) {
      console.warn('Today\'s events fetch failed (non-critical):', err.message);
    }
  }, [token]);

  // HTTP fallback for when WebSocket is not available
  const fetchCommandPost = useCallback(async (eventOverride) => {
    const evName = eventOverride || selectedEvent;
    if (!evName) return;
    setLoading(true);
    try {
      const { data: cpData } = await axios.get(
        `${API_URL}/api/reports/command-post?event_name=${encodeURIComponent(evName)}`,
        {}
      );
      setData(cpData);
      setLastRefresh(new Date());
    } catch (err) {
      console.warn('Command post fetch failed:', err.message);
    }
    finally { setLoading(false); }
  }, [selectedEvent, token]);

  // WebSocket connection management
  const connectWebSocket = useCallback((eventName) => {
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }

    if (!eventName) {
      setWsConnected(false);
      return;
    }

    const wsUrl = `${WS_URL}/api/ws/command-post/${encodeURIComponent(eventName)}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setWsConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const cpData = JSON.parse(event.data);
        setData(cpData);
        setLastRefresh(new Date());
        setLoading(false);
      } catch (err) {
        console.warn('WS message parse failed:', err.message);
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      wsRef.current = null;
      // Auto-reconnect after 5s if still on the same event
      if (autoRefresh) {
        reconnectTimer.current = setTimeout(() => {
          connectWebSocket(eventName);
        }, 5000);
      }
    };

    ws.onerror = () => {
      // Will trigger onclose, which handles reconnection
      console.warn('WS connection error, falling back to polling');
    };

    wsRef.current = ws;
  }, [autoRefresh]);

  useEffect(() => { fetchEvents(); fetchTodaysEvents(); }, [fetchEvents, fetchTodaysEvents]);

  // Connect WS when event changes
  useEffect(() => {
    if (selectedEvent) {
      setLoading(true);
      connectWebSocket(selectedEvent);
    } else {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setWsConnected(false);
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
    };
  }, [selectedEvent, connectWebSocket]);

  // Polling fallback: only poll when WebSocket is NOT connected
  useEffect(() => {
    if (!autoRefresh || !selectedEvent || wsConnected) return;
    const interval = setInterval(() => fetchCommandPost(), 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, selectedEvent, wsConnected, fetchCommandPost]);

  // Auto-switch between today's events every 15s
  useEffect(() => {
    if (!autoSwitch || todaysEvents.length < 2) return;
    const interval = setInterval(() => {
      autoSwitchIndex.current = (autoSwitchIndex.current + 1) % todaysEvents.length;
      const nextEvent = todaysEvents[autoSwitchIndex.current].event_name;
      setSelectedEvent(nextEvent);
    }, 15000);
    return () => clearInterval(interval);
  }, [autoSwitch, todaysEvents]);

  const handleManualRefresh = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send('refresh');
    } else {
      fetchCommandPost();
    }
  };

  const getDispositionLabel = (p) => {
    if (p.recently_closed) return { label: 'Completed', cls: 'bg-emerald-600 text-white' };
    if (p.is_transported) return { label: 'Transport', cls: 'bg-blue-600 text-white' };
    if (p.rma_signature) return { label: 'RMA', cls: 'bg-orange-500 text-white' };
    return { label: 'In Triage', cls: 'bg-slate-500 text-white' };
  };

  const centerNames = data?.center_groups ? Object.keys(data.center_groups) : [];

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="command-post-page">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" data-testid="back-to-dashboard">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800 rounded-sm">
                  <ArrowLeft size={18} className="mr-2" /> Dashboard
                </Button>
              </Link>
              <div className="h-6 w-px bg-slate-700" />
              <div className="flex items-center gap-2">
                <img src={LOGO_1} alt="Logo" className="h-7 object-contain brightness-0 invert opacity-70" />
                <img src={LOGO_2} alt="Logo" className="h-7 object-contain brightness-0 invert opacity-70" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* WebSocket status indicator */}
              <div className={`flex items-center gap-1.5 text-xs font-medium ${wsConnected ? 'text-cyan-400' : 'text-slate-500'}`} data-testid="ws-status">
                {wsConnected ? <WifiHigh size={14} /> : <WifiSlash size={14} />}
                {wsConnected ? 'Real-Time' : 'Polling'}
              </div>
              <div className="h-4 w-px bg-slate-700" />
              {/* Auto-switch badge */}
              {todaysEvents.length >= 2 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-xs rounded-sm ${autoSwitch ? 'text-amber-400 hover:text-amber-300' : 'text-slate-500 hover:text-slate-300'} hover:bg-slate-800`}
                  onClick={() => setAutoSwitch(!autoSwitch)}
                  data-testid="toggle-auto-switch"
                >
                  <ArrowsClockwise size={14} className={`mr-1 ${autoSwitch ? 'animate-spin' : ''}`} />
                  {autoSwitch ? 'Auto-Switching' : 'Auto-Switch'}
                </Button>
              )}
              <div className={`flex items-center gap-1.5 text-xs font-medium ${autoRefresh ? 'text-green-400' : 'text-slate-500'}`}>
                <Circle size={8} weight="fill" className={autoRefresh ? 'animate-pulse' : ''} />
                {autoRefresh ? 'LIVE' : 'PAUSED'}
              </div>
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800 rounded-sm text-xs" onClick={() => setAutoRefresh(!autoRefresh)} data-testid="toggle-auto-refresh">
                {autoRefresh ? 'Pause' : 'Resume'}
              </Button>
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800 rounded-sm" onClick={handleManualRefresh} disabled={loading || !selectedEvent} data-testid="manual-refresh">
                <ArrowClockwise size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Title + Event Selector */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Lightning size={24} className="text-amber-400" />
              Event Command Post
            </h1>
            {lastRefresh && (
              <p className="text-xs text-slate-500 mt-1">
                Last updated: {lastRefresh.toLocaleTimeString('en-US', { timeZone: 'America/New_York' })}
                {wsConnected && <span className="ml-2 text-cyan-400/70">| WebSocket connected</span>}
                {data && <span className="ml-2">| Showing open forms only</span>}
              </p>
            )}
          </div>
          <Select value={selectedEvent} onValueChange={(v) => { setSelectedEvent(v); setAutoSwitch(false); }}>
            <SelectTrigger className="w-72 bg-slate-800 border-slate-700 text-white rounded-sm" data-testid="event-selector">
              <SelectValue placeholder="Select an event..." />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {events.map((ev) => (
                <SelectItem key={ev.id} value={ev.name} className="text-white focus:bg-slate-700 focus:text-white">
                  {ev.name}
                  {todaysEvents.find(t => t.event_name === ev.name) && (
                    <span className="ml-2 text-xs text-amber-400">(today)</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!selectedEvent && (
          <div className="text-center py-24">
            <Lightning size={64} className="mx-auto text-slate-700 mb-4" />
            <p className="text-slate-500 text-lg">Select an event to view the live command post</p>
            {todaysEvents.length >= 2 && (
              <p className="text-amber-400/60 text-sm mt-2">{todaysEvents.length} events active today — enable auto-switch to cycle between them</p>
            )}
          </div>
        )}

        {data && selectedEvent && (
          <div className="space-y-6">
            {/* Key Metrics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Patients</p>
                  <p className="text-4xl font-black text-white mt-1" data-testid="cp-total-patients">{data.total_patients}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wider text-blue-400">Transported</p>
                  <p className="text-4xl font-black text-blue-400 mt-1" data-testid="cp-transported">{data.transported}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{data.transport_rate}% rate</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wider text-orange-400">RMA</p>
                  <p className="text-4xl font-black text-orange-400 mt-1" data-testid="cp-rma">{data.rma}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">In Triage</p>
                  <p className="text-4xl font-black text-white mt-1" data-testid="cp-on-scene">{data.on_scene}</p>
                </CardContent>
              </Card>
            </div>

            {/* Triage Breakdown */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-slate-200 uppercase tracking-wider">Triage Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-3">
                  {TRIAGE_CONFIG.map(({ color, bg, text, label }) => (
                    <div key={color} className={`${bg} ${text} rounded-sm p-4 text-center`} data-testid={`cp-triage-${color.toLowerCase()}`}>
                      <p className="text-3xl font-black">{data.triage[color]}</p>
                      <p className="text-xs font-semibold mt-1 opacity-90">{color}</p>
                      <p className="text-[10px] opacity-70">{label}</p>
                    </div>
                  ))}
                </div>
                {data.total_patients > 0 && (
                  <div className="flex h-5 rounded-sm overflow-hidden mt-4">
                    {TRIAGE_CONFIG.map(({ color, bg }) => {
                      const pct = (data.triage[color] / data.total_patients) * 100;
                      return pct > 0 ? (
                        <div key={color} className={`${bg} flex items-center justify-center text-[10px] font-bold`} style={{ width: `${pct}%` }}>
                          {pct >= 8 ? `${Math.round(pct)}%` : ''}
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Form Status + Board Count */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-400">Open Forms (on board)</p>
                  <p className="text-4xl font-black text-amber-400 mt-1" data-testid="cp-open-forms">{data.open_forms}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wider text-green-400">Closed Forms</p>
                  <p className="text-4xl font-black text-green-400 mt-1" data-testid="cp-closed-forms">{data.closed_forms}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="pt-5 pb-4 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Triage Centers</p>
                  <p className="text-4xl font-black text-white mt-1">{centerNames.length}</p>
                </CardContent>
              </Card>
            </div>

            {/* Triage Center Sections */}
            {centerNames.length > 0 ? (
              <div className="space-y-4">
                {centerNames.map((centerName) => {
                  const patients = data.center_groups[centerName] || [];
                  return (
                    <Card key={centerName} className="bg-slate-800 border-slate-700" data-testid={`center-section-${centerName.replace(/\s+/g, '-').toLowerCase()}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base text-slate-200 flex items-center gap-2">
                            <MapPin size={18} className="text-blue-400" />
                            {centerName}
                          </CardTitle>
                          <div className="flex items-center gap-3">
                            <div className="flex gap-1">
                              {TRIAGE_CONFIG.map(({ color, bg, text }) => {
                                const count = patients.filter(p => p.tag_color === color).length;
                                return count > 0 ? (
                                  <span key={color} className={`${bg} ${text} px-1.5 py-0.5 rounded text-xs font-bold`}>{color[0]}:{count}</span>
                                ) : null;
                              })}
                            </div>
                            <span className="text-sm font-bold text-white">{patients.length} <span className="text-slate-400 font-normal">patients</span></span>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {patients.length === 0 ? (
                          <p className="text-slate-500 text-sm text-center py-4">No open patients in this area</p>
                        ) : (
                          <PatientTable patients={patients} getDispositionLabel={getDispositionLabel} />
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              data.board_patients && data.board_patients.length > 0 && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base text-slate-200 uppercase tracking-wider">Open Patients</CardTitle>
                    <CardDescription className="text-slate-500">{data.board_patients.length} patients with open forms</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <PatientTable patients={data.board_patients} getDispositionLabel={getDispositionLabel} scrollable />
                  </CardContent>
                </Card>
              )
            )}

            {/* Empty board state */}
            {data.board_patients && data.board_patients.length === 0 && (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="py-12 text-center">
                  <p className="text-slate-500">All patient forms are closed for this event.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function PatientTable({ patients, getDispositionLabel, scrollable }) {
  return (
    <div className={`overflow-x-auto ${scrollable ? 'max-h-[400px] overflow-y-auto' : ''}`}>
      <table className={`w-full text-sm`} data-testid="cp-recent-table">
        <thead className={scrollable ? 'sticky top-0 bg-slate-800' : ''}>
          <tr className="border-b border-slate-700">
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">Patient</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">Triage</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">Status</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">Provider</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">Contact</th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
              <Timer size={14} className="inline mr-1" />Duration
            </th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700/50">
          {patients.map((p) => {
            const disp = getDispositionLabel(p);
            const triageCfg = TRIAGE_CONFIG.find(t => t.color === p.tag_color);
            return (
              <tr key={p.id} className={`hover:bg-slate-700/30 ${p.recently_closed ? 'opacity-50' : ''}`} data-testid={`cp-patient-${p.id}`}>
                <td className="px-3 py-2 text-white font-medium">
                  {p.first_name} {p.last_name}
                  {p.recently_closed && <span className="ml-2 text-[10px] text-emerald-400 font-normal">(closing soon)</span>}
                </td>
                <td className="px-3 py-2">
                  {triageCfg ? (
                    <span className={`${triageCfg.bg} ${triageCfg.text} px-2 py-0.5 rounded text-xs font-bold`}>{p.tag_color}</span>
                  ) : <span className="text-slate-500">-</span>}
                </td>
                <td className="px-3 py-2">
                  <span className={`${disp.cls} px-2 py-0.5 rounded text-xs font-semibold`}>{disp.label}</span>
                </td>
                <td className="px-3 py-2 text-slate-400 text-xs">{p.created_by_name || '-'}</td>
                <td className="px-3 py-2 text-slate-400 text-xs">{p.patient_contact_time || '-'}</td>
                <td className="px-3 py-2">
                  <span className={`text-sm font-bold ${getDurationColor(p.duration_minutes)}`}>
                    {formatDuration(p.duration_minutes)}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <Link to={`/patient/${p.id}`}>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-white" data-testid={`cp-view-${p.id}`}>
                      <Eye size={14} />
                    </Button>
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
